import { useState } from "react";
import { useRouteError, isRouteErrorResponse, useNavigate, Link } from "react-router-dom";
import { AlertTriangle, Home, RotateCcw, Bug, Copy, ChevronDown } from "lucide-react";
import { useLogging } from "@/components/contexts/LoggingContext";
import { isModuleLoadError, retryModuleLoad } from "@/lib/module-recovery";

export default function ErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();
    const { userback } = useLogging();
    const [showDetails, setShowDetails] = useState(false);
    const [retryMessage, setRetryMessage] = useState("");

    const handleImportRetry = () => {
        try {
            const result = retryModuleLoad(navigator.onLine, window.sessionStorage, () => {
                window.location.reload();
            });
            const messages = {
                reloading: "Reloading this page…",
                offline: "You appear to be offline. Reconnect, then try again.",
                limited:
                    "A retry was already attempted. Wait five minutes before trying again, or report the problem if it continues.",
                "storage-unavailable":
                    "A safe retry could not be saved. Use your browser's refresh button to retry this page.",
            };
            setRetryMessage(messages[result]);
        } catch {
            setRetryMessage("Use your browser's refresh button to retry this page.");
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleGoHome = () => {
        navigate("/");
    };

    const handleReload = () => {
        window.location.reload();
    };

    const handleCopyError = () => {
        let errorText = `
=== Error Report ===
Time: ${new Date().toISOString()}
URL: ${window.location.href}

`;

        if (isRouteErrorResponse(error)) {
            errorText += `Status: ${error.status} ${error.statusText}
Data: ${error.data}
`;
        } else if (error instanceof Error) {
            errorText += `Error Message: ${error.message}

Stack Trace:
${error.stack || "No stack trace available"}
`;
        } else {
            errorText += `Error: ${String(error)}
`;
        }

        errorText += `
User Agent: ${navigator.userAgent}
==================
        `.trim();

        navigator.clipboard.writeText(errorText).then(() => {
            alert("Error details copied to clipboard! Please paste this when reporting the bug.");
        });
    };

    const handleReportBug = () => {
        if (userback) {
            userback.setData({
                error: error instanceof Error ? error.message : String(error),
                error_page: window.location.href,
            });
            userback.openForm("bug", "screenshot", import.meta.env.VITE_USERBACK_PROJECT_KEY);
            return;
        }

        handleCopyError();
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    if (isModuleLoadError(error)) {
        return (
            <main className="min-h-screen flex items-center justify-center p-6">
                <div className="max-w-lg space-y-4">
                    <h1 className="text-2xl font-bold">This page could not be downloaded</h1>
                    <p>
                        Check your connection and retry. An app update or a temporary download
                        problem may have interrupted loading.
                    </p>
                    <p>
                        Retry reloads your current page. Your sign-in and verification session will
                        be kept. You may need to re-enter an unsent verification code.
                    </p>
                    <button className="underline" onClick={handleImportRetry}>
                        Retry loading this page
                    </button>
                    <p role="status">{retryMessage}</p>
                    <button className="underline" onClick={handleReportBug}>
                        Report problem
                    </button>
                </div>
            </main>
        );
    }

    // Handle 404 / Route Error Response
    if (isRouteErrorResponse(error)) {
        if (error.status === 404) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
                    <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden text-center">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
                            <div className="flex items-center justify-center gap-4 text-white">
                                <div className="text-6xl font-bold">404</div>
                            </div>
                        </div>
                        <div className="p-8">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                                Page Not Found
                            </h1>
                            <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                                Sorry, we couldn't find the page you're looking for.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleGoBack}
                                    className="flex-1 flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 px-4 py-3 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    Go Back
                                </button>
                                <Link
                                    to="/"
                                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
                                >
                                    <Home className="h-4 w-4" />
                                    Go Home
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Other route errors (non-404)
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
                <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6">
                        <div className="flex items-center gap-4 text-white">
                            <div className="bg-white/20 p-3 rounded-full">
                                <AlertTriangle className="h-8 w-8" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold">
                                    {error.status} {error.statusText}
                                </h1>
                                <p className="text-orange-100 mt-1">Something went wrong</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                            <p className="text-sm text-orange-700 dark:text-orange-300">
                                {error.data || "An error occurred while processing your request."}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleGoBack}
                                className="flex-1 flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 px-4 py-3 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Go Back
                            </button>
                            <button
                                onClick={handleGoHome}
                                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
                            >
                                <Home className="h-4 w-4" />
                                Go Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Handle actual errors (Error instances)
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
            <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-red-500 to-red-600 p-6">
                    <div className="flex items-center gap-4 text-white">
                        <div className="bg-white/20 p-3 rounded-full animate-pulse">
                            <AlertTriangle className="h-8 w-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Something went wrong</h1>
                            <p className="text-red-100 mt-1">
                                We've encountered an unexpected error
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Error Message Box */}
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
                            Error Details:
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-300 font-mono break-words">
                            {error instanceof Error
                                ? error.message
                                : String(error) || "An unknown error occurred"}
                        </p>
                    </div>

                    {/* Bug Report Section - Eye-catching */}
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg p-5 shadow-lg">
                        <div className="flex items-start gap-4">
                            <div className="bg-yellow-400 dark:bg-yellow-600 p-2.5 rounded-full">
                                <Bug className="h-6 w-6 text-yellow-900 dark:text-yellow-100" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-yellow-900 dark:text-yellow-100 mb-2">
                                    🐛 Please Report This Bug!
                                </h3>
                                <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-4 leading-relaxed">
                                    Help us fix this issue! Click below to copy error details and
                                    report this bug to our support team. Your feedback is
                                    invaluable.
                                </p>
                                <button
                                    onClick={handleReportBug}
                                    className="bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors flex items-center gap-2 shadow-md"
                                >
                                    <Bug className="h-4 w-4" />
                                    Report Bug & Copy Details
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                            onClick={handleGoBack}
                            className="flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 px-4 py-3 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        >
                            <RotateCcw className="h-4 w-4" />
                            Go Back
                        </button>
                        <button
                            onClick={handleGoHome}
                            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors"
                        >
                            <Home className="h-4 w-4" />
                            Go Home
                        </button>
                        <button
                            onClick={handleReload}
                            className="flex items-center justify-center gap-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-3 rounded-lg font-medium hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
                        >
                            <RotateCcw className="h-4 w-4" />
                            Reload Page
                        </button>
                    </div>

                    {/* Copy Error Button */}
                    <button
                        onClick={handleCopyError}
                        className="w-full flex items-center justify-center gap-2 bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-600"
                    >
                        <Copy className="h-4 w-4" />
                        Copy Error Details to Clipboard
                    </button>

                    {/* Technical Details Expandable */}
                    <div className="border-t dark:border-slate-700 pt-4">
                        <button
                            onClick={toggleDetails}
                            className="flex items-center justify-between w-full text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors group"
                        >
                            <span className="font-medium">Technical Details (for developers)</span>
                            <ChevronDown
                                className={`h-4 w-4 transition-transform ${
                                    showDetails ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        {showDetails && error instanceof Error && (
                            <div className="mt-4 space-y-4">
                                <div>
                                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                        Stack Trace:
                                    </p>
                                    <pre className="text-xs bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 p-3 rounded-lg overflow-x-auto max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700">
                                        {error.stack || "No stack trace available"}
                                    </pre>
                                </div>

                                <div>
                                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                        Additional Information:
                                    </p>
                                    <div className="text-xs bg-slate-100 dark:bg-slate-900 p-3 rounded-lg space-y-1 border border-slate-200 dark:border-slate-700">
                                        <p className="text-slate-700 dark:text-slate-300">
                                            <span className="font-semibold">Current URL:</span>{" "}
                                            {window.location.href}
                                        </p>
                                        <p className="text-slate-700 dark:text-slate-300">
                                            <span className="font-semibold">Timestamp:</span>{" "}
                                            {new Date().toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
